import React, { useEffect, useMemo, useState } from "react";
import {
  getVaccineReminderSettings,
  saveVaccineReminderSettings,
  sendTodayVaccineReminders,
} from "../../../shared/api/animal/emailReminderService";
import { listVaccineTasksByProjectType } from "../../../shared/api/animal/vaccineService";

const pageStyle = {
  minHeight: "100vh",
  color: "#f8fafc",
};

const cardStyle = {
  background: "linear-gradient(180deg, rgba(3,12,33,0.96), rgba(2,10,28,0.96))",
  border: "1px solid rgba(99,102,241,0.18)",
  borderRadius: "18px",
  padding: "20px",
  boxShadow: "0 8px 30px rgba(0,0,0,0.22)",
};

const infoCardStyle = {
  background: "linear-gradient(180deg, rgba(29,26,85,0.55), rgba(17,24,39,0.75))",
  border: "1px solid rgba(99,102,241,0.35)",
  borderRadius: "18px",
  padding: "20px",
  boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
};

const labelStyle = {
  marginBottom: "8px",
  fontWeight: 600,
  color: "rgba(255,255,255,0.88)",
  fontSize: "15px",
};

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: "12px",
  border: "1px solid rgba(255,255,255,0.12)",
  outline: "none",
  background: "rgba(7,18,42,0.92)",
  color: "#f8fafc",
  fontSize: "15px",
};

const selectStyle = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: "12px",
  border: "1px solid rgba(255,255,255,0.12)",
  outline: "none",
  background: "rgba(7,18,42,0.92)",
  color: "#f8fafc",
  fontSize: "15px",
  appearance: "none",
};

const buttonBase = {
  padding: "11px 16px",
  borderRadius: "12px",
  border: "1px solid rgba(255,255,255,0.08)",
  cursor: "pointer",
  fontWeight: 700,
  fontSize: "14px",
  transition: "all 0.18s ease",
};

const primaryButton = {
  ...buttonBase,
  background: "linear-gradient(135deg,#4f46e5,#06b6d4)",
  color: "#fff",
  boxShadow: "0 0 18px rgba(79,70,229,0.24)",
};

const greenButton = {
  ...buttonBase,
  background: "linear-gradient(135deg,#059669,#10b981)",
  color: "#fff",
};

const amberButton = {
  ...buttonBase,
  background: "linear-gradient(135deg,#d97706,#f59e0b)",
  color: "#fff",
};

const darkButton = {
  ...buttonBase,
  background: "rgba(255,255,255,0.06)",
  color: "#fff",
};

function formatType(type) {
  if (!type) return "-";
  switch (String(type).toUpperCase()) {
    case "PRIMARY":
      return "Primary Vaccine";
    case "BOOSTER":
      return "Booster";
    case "REPEAT":
      return "Repeat Dose";
    default:
      return type;
  }
}

function formatStatus(status) {
  if (!status) return "-";
  return String(status).replaceAll("_", " ");
}

export default function EmailReminderPage() {
  const [settings, setSettings] = useState({
    enabled: true,
    time: "09:00",
    frontendBaseUrl: window.location.origin,
    extraRecipients: "",
  });

  const [filters, setFilters] = useState({
    species: "",
    projectType: "FATTENING",
    date: new Date().toISOString().slice(0, 10),
    status: "TODAY",
  });

  const [tasks, setTasks] = useState([]);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);

  const [message, setMessage] = useState("");
  const [dispatchResult, setDispatchResult] = useState(null);

  const pageTitle = useMemo(() => "Email Reminder", []);

  async function loadSettings() {
    try {
      setLoadingSettings(true);
      const data = await getVaccineReminderSettings();
      setSettings({
        enabled: data?.enabled ?? true,
        time: data?.time || "09:00",
        frontendBaseUrl: data?.frontendBaseUrl || window.location.origin,
        extraRecipients: data?.extraRecipients || "",
      });
    } catch (err) {
      console.error(err);
      console.error("Full backend error:", err?.response?.data);
      setMessage(err?.response?.data?.message || "Failed to load reminder settings.");
    } finally {
      setLoadingSettings(false);
    }
  }

  async function loadTasks() {
    try {
      setLoadingTasks(true);
      setMessage("");

      if (!filters.projectType) {
        setTasks([]);
        setMessage("Please select a project type.");
        return;
      }

      const data = await listVaccineTasksByProjectType({
        species: filters.species || undefined,
        projectType: filters.projectType || undefined,
        date: filters.date || undefined,
        status: filters.status || undefined,
      });

      setTasks(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      console.error("Full backend error:", err?.response?.data);
      setMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to load vaccine tasks."
      );
      setTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  }

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    loadTasks();
  }, [filters.species, filters.projectType, filters.date, filters.status]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSaveSettings(e) {
    e.preventDefault();
    try {
      setSavingSettings(true);
      setMessage("");

      const saved = await saveVaccineReminderSettings({
        ...settings,
        frontendBaseUrl: settings.frontendBaseUrl || window.location.origin,
      });

      setSettings({
        enabled: saved?.enabled ?? true,
        time: saved?.time || "09:00",
        frontendBaseUrl: saved?.frontendBaseUrl || window.location.origin,
        extraRecipients: saved?.extraRecipients || "",
      });

      setMessage("Vaccine reminder settings saved successfully.");
    } catch (err) {
      console.error(err);
      console.error("Full backend error:", err?.response?.data);
      setMessage(err?.response?.data?.message || "Failed to save reminder settings.");
    } finally {
      setSavingSettings(false);
    }
  }

  async function handleSendNow(forceResend = false) {
    try {
      setSendingNow(true);
      setMessage("");

      const result = await sendTodayVaccineReminders(forceResend);
      setDispatchResult(result);

      const sent = result?.sentCount ?? 0;
      setMessage(`Reminder process completed. Sent: ${sent}`);
      await loadTasks();
    } catch (err) {
      console.error(err);
      console.error("Full backend error:", err?.response?.data);
      setMessage(err?.response?.data?.message || "Failed to send vaccine reminders.");
    } finally {
      setSendingNow(false);
    }
  }

  return (
    <div className="email-reminder-page" style={pageStyle}>
      <div style={{ marginBottom: "22px" }}>
        <h1
          style={{
            margin: 0,
            fontSize: "44px",
            lineHeight: 1.1,
            fontWeight: 800,
            color: "#f8fafc",
            letterSpacing: "-0.03em",
          }}
        >
          {pageTitle}
        </h1>
        <p
          style={{
            marginTop: "10px",
            color: "rgba(255,255,255,0.46)",
            fontSize: "17px",
            maxWidth: "900px",
            lineHeight: 1.6,
          }}
        >
          Manage automated email reminders for vaccine tasks. Later we can extend this same page for food and medicine.
        </p>
      </div>

      <div style={{ ...infoCardStyle, marginBottom: "20px" }}>
        <h3
          style={{
            margin: 0,
            marginBottom: "10px",
            color: "#ffffff",
            fontSize: "18px",
            fontWeight: 700,
          }}
        >
          Important
        </h3>
        <p
          style={{
            margin: 0,
            color: "rgba(255,255,255,0.82)",
            fontSize: "15px",
            lineHeight: 1.8,
          }}
        >
          Vaccine reminders are sent based on your configured time. The system will remind the admin about due vaccine tasks for the selected project type and due date.
        </p>
      </div>

      {message ? (
        <div
          style={{
            marginBottom: "18px",
            padding: "13px 15px",
            borderRadius: "12px",
            background: "rgba(37,99,235,0.14)",
            border: "1px solid rgba(96,165,250,0.28)",
            color: "#bfdbfe",
            fontWeight: 500,
          }}
        >
          {message}
        </div>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.12fr 0.88fr",
          gap: "20px",
          alignItems: "start",
        }}
      >
        <div style={cardStyle}>
          <div style={{ marginBottom: "16px" }}>
            <h2 style={{ margin: 0, fontSize: "20px", color: "#fff", fontWeight: 700 }}>
              Vaccine Reminder Settings
            </h2>
            <p style={{ marginTop: "8px", color: "rgba(255,255,255,0.45)", lineHeight: 1.6 }}>
              Configure when the admin should receive the vaccine reminder mail.
            </p>
          </div>

          <form onSubmit={handleSaveSettings}>
            <div style={{ display: "grid", gap: "16px" }}>
              <label>
                <div style={labelStyle}>Reminder Enabled</div>
                <select
                  style={selectStyle}
                  value={String(settings.enabled)}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      enabled: e.target.value === "true",
                    }))
                  }
                  disabled={loadingSettings || savingSettings}
                >
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </select>
              </label>

              <label>
                <div style={labelStyle}>Reminder Time</div>
                <input
                  type="time"
                  style={inputStyle}
                  value={settings.time || "09:00"}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      time: e.target.value,
                    }))
                  }
                  disabled={loadingSettings || savingSettings}
                />
              </label>

              <label>
                <div style={labelStyle}>Frontend Base URL</div>
                <input
                  type="text"
                  style={inputStyle}
                  placeholder="http://localhost:3000"
                  value={settings.frontendBaseUrl || ""}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      frontendBaseUrl: e.target.value,
                    }))
                  }
                  disabled={loadingSettings || savingSettings}
                />
              </label>

              <label>
                <div style={labelStyle}>Extra Recipient Emails</div>
                <input
                  type="text"
                  style={inputStyle}
                  placeholder="owner@gmail.com,manager@gmail.com"
                  value={settings.extraRecipients || ""}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      extraRecipients: e.target.value,
                    }))
                  }
                  disabled={loadingSettings || savingSettings}
                />
              </label>

              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "6px" }}>
                <button type="submit" style={primaryButton} disabled={savingSettings}>
                  {savingSettings ? "Saving..." : "Save Settings"}
                </button>

                <button
                  type="button"
                  style={greenButton}
                  onClick={() => handleSendNow(false)}
                  disabled={sendingNow}
                >
                  {sendingNow ? "Sending..." : "Send Today Reminder"}
                </button>

                <button
                  type="button"
                  style={amberButton}
                  onClick={() => handleSendNow(true)}
                  disabled={sendingNow}
                >
                  {sendingNow ? "Sending..." : "Force Resend"}
                </button>
              </div>
            </div>
          </form>
        </div>

        <div style={cardStyle}>
          <div style={{ marginBottom: "16px" }}>
            <h2 style={{ margin: 0, fontSize: "20px", color: "#fff", fontWeight: 700 }}>
              Latest Reminder Result
            </h2>
            <p style={{ marginTop: "8px", color: "rgba(255,255,255,0.45)", lineHeight: 1.6 }}>
              Shows what happened when the reminder dispatch was triggered.
            </p>
          </div>

          {dispatchResult ? (
            <div style={{ display: "grid", gap: "12px", color: "rgba(255,255,255,0.88)" }}>
              <div><strong style={{ color: "#fff" }}>Enabled:</strong> {String(dispatchResult.enabled)}</div>
              <div><strong style={{ color: "#fff" }}>Configured Time:</strong> {dispatchResult.configuredTime || "-"}</div>
              <div><strong style={{ color: "#fff" }}>Scanned Tasks:</strong> {dispatchResult.scannedTasks ?? 0}</div>
              <div><strong style={{ color: "#fff" }}>Eligible Tasks:</strong> {dispatchResult.eligibleTasks ?? 0}</div>
              <div><strong style={{ color: "#fff" }}>Sent Count:</strong> {dispatchResult.sentCount ?? 0}</div>
              <div><strong style={{ color: "#fff" }}>Skipped Count:</strong> {dispatchResult.skippedCount ?? 0}</div>

              <div>
                <strong style={{ color: "#fff" }}>Recipients:</strong>
                <div style={{ marginTop: "6px", color: "rgba(255,255,255,0.72)", lineHeight: 1.6 }}>
                  {Array.isArray(dispatchResult.recipients) && dispatchResult.recipients.length
                    ? dispatchResult.recipients.join(", ")
                    : "No recipients"}
                </div>
              </div>

              <div>
                <strong style={{ color: "#fff" }}>Messages:</strong>
                <ul style={{ marginTop: "8px", paddingLeft: "18px", color: "rgba(255,255,255,0.72)" }}>
                  {(dispatchResult.messages || []).map((msg, idx) => (
                    <li key={idx} style={{ marginBottom: "4px" }}>{msg}</li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <div style={{ color: "rgba(255,255,255,0.45)" }}>
              No reminder action triggered yet.
            </div>
          )}
        </div>
      </div>

      <div style={{ ...cardStyle, marginTop: "20px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "12px",
            alignItems: "center",
            flexWrap: "wrap",
            marginBottom: "16px",
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: "20px", color: "#fff", fontWeight: 700 }}>
              Vaccine Tasks Preview
            </h2>
            <p style={{ marginTop: "8px", color: "rgba(255,255,255,0.45)", lineHeight: 1.6 }}>
              These are the tasks that your email reminder system will work with.
            </p>
          </div>

          <button
            type="button"
            style={darkButton}
            onClick={loadTasks}
            disabled={loadingTasks}
          >
            {loadingTasks ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(180px, 1fr))",
            gap: "14px",
            marginBottom: "18px",
          }}
        >
          <div>
            <div style={labelStyle}>Species</div>
            <select
              style={selectStyle}
              value={filters.species}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, species: e.target.value }))
              }
            >
              <option value="">All</option>
              <option value="GOAT">GOAT</option>
              <option value="SHEEP">SHEEP</option>
              <option value="COW">COW</option>
              <option value="BUFFALO">BUFFALO</option>
            </select>
          </div>

          <div>
            <div style={labelStyle}>Project Type</div>
            <select
              style={selectStyle}
              value={filters.projectType}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, projectType: e.target.value }))
              }
            >
              <option value="FATTENING">FATTENING</option>
              <option value="BREEDING">BREEDING</option>
            </select>
          </div>

          <div>
            <div style={labelStyle}>Date</div>
            <input
              type="date"
              style={inputStyle}
              value={filters.date}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, date: e.target.value }))
              }
            />
          </div>

          <div>
            <div style={labelStyle}>Status</div>
            <select
              style={selectStyle}
              value={filters.status}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, status: e.target.value }))
              }
            >
              <option value="">All</option>
              <option value="TODAY">TODAY</option>
              <option value="UPCOMING">UPCOMING</option>
              <option value="COMPLETED">COMPLETED</option>
              <option value="MISSED">MISSED</option>
            </select>
          </div>
        </div>

        <div
          style={{
            overflowX: "auto",
            borderRadius: "14px",
            border: "1px solid rgba(255,255,255,0.08)",
            background: "rgba(3,10,26,0.92)",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              background: "transparent",
            }}
          >
            <thead>
              <tr style={{ background: "rgba(255,255,255,0.04)" }}>
                <th style={thStyle}>Batch</th>
                <th style={thStyle}>Project Type</th>
                <th style={thStyle}>Species</th>
                <th style={thStyle}>Vaccine</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Task Date</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Reminder Sent</th>
              </tr>
            </thead>
            <tbody>
              {tasks.length === 0 ? (
                <tr>
                  <td colSpan="8" style={emptyTdStyle}>
                    {loadingTasks ? "Loading vaccine tasks..." : "No vaccine tasks found."}
                  </td>
                </tr>
              ) : (
                tasks.map((task) => (
                  <tr key={task.id} style={{ background: "transparent" }}>
                    <td style={tdStyle}>{task.batchName || "-"}</td>
                    <td style={tdStyle}>{task.projectType || "-"}</td>
                    <td style={tdStyle}>{task.species || "-"}</td>
                    <td style={tdStyle}>{task.vaccineName || "-"}</td>
                    <td style={tdStyle}>{formatType(task.taskType)}</td>
                    <td style={tdStyle}>{task.taskDate || "-"}</td>
                    <td style={tdStyle}>{formatStatus(task.status)}</td>
                    <td style={tdStyle}>{task.reminderSentAt || "-"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const thStyle = {
  textAlign: "left",
  padding: "14px 12px",
  borderBottom: "1px solid rgba(255,255,255,0.08)",
  fontSize: "14px",
  color: "rgba(255,255,255,0.75)",
  fontWeight: 700,
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "14px 12px",
  borderBottom: "1px solid rgba(255,255,255,0.05)",
  fontSize: "14px",
  color: "rgba(255,255,255,0.88)",
  whiteSpace: "nowrap",
};

const emptyTdStyle = {
  padding: "24px",
  textAlign: "center",
  color: "rgba(255,255,255,0.45)",
};
